import { forwardRef, useImperativeHandle } from "react";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";

const ChartExportAsPNG = forwardRef((props, ref) => {
    const {
        targetId,
        chartData = [],
        chartType = "pie",
        fileNamePrefix = "chart",
        targetWidth = null,
        padding = 20,
        border = 1,
        scale = 2,
        backgroundColor = "#fff",
        imageTitle = "Chart Export",
        imageSource = "",
        titleFont = "bold 28px Arial",
        sourceFont = "italic 20px Arial",
        captionColor = "#222",
        legendFont = "24px Arial",
        cellPadding = 15,
        tableHeaderColor = "#eee",
        tableBorderColor = "#aaa",
        innerPadding = 20,
        tableFont = "16px Arial",
        LegendFirstColumn = "Legend",
        showColorBlock = true,
        colorMap = [],
        table = true,  // NEW: Enable or disable table rendering
    } = props;

    useImperativeHandle(ref, () => ({
        download: handleDownloadClick,
    }));

    const computeScale = (target, targetWidth, defaultScale) => {
        if (!targetWidth) return defaultScale;
        const naturalW = target.offsetWidth;
        if (!naturalW || naturalW === 0) return defaultScale;
        const ratio = targetWidth / naturalW;
        return ratio * defaultScale;
    };

    const extractFontSize = (fontStr) => {
        const match = fontStr.match(/(\d+)px/);
        return match ? parseInt(match[1], 10) : 20;
    };

    const getMappedColor = (label, fallback) => {
        const entry = colorMap.find(([k]) => k === label);
        return entry ? entry[1] : fallback;
    };

    const handleDownloadClick = () => {
        const target = document.getElementById(targetId);
        if (!target) {
            console.warn(`Element with ID "${targetId}" not found.`);
            return;
        }

        html2canvas(target, {
            scale: computeScale(target, targetWidth, scale)
        }).then((chartCanvas) => {
            const chartW = chartCanvas.width;
            const chartH = chartCanvas.height;

            const isMulti = chartData.some(d => d.values);
            const totalVal = isMulti ? 0 : chartData.reduce((s, d) => s + (d.value || 0), 0);

            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");
            tempCtx.font = tableFont;

            const { tableW, columns, colWidths, legendColW } = computeTableWidth(tempCtx, chartData, isMulti);

            const fontSizePx = extractFontSize(tableFont);
            const rowH = fontSizePx + 20;

            const captionGap = 20;
            const titleH = imageTitle ? 25 : 0;
            const sourceH = imageSource ? 20 : 0;
            const betweenCaptionGap = imageTitle && imageSource ? 5 : 0;
            const captionBlockH = titleH + sourceH + betweenCaptionGap;

            let contentW, contentH;

            if (chartType === "pie") {
                contentW = innerPadding + chartW + (table ? innerPadding + tableW : 0) + innerPadding;
                contentH = innerPadding + Math.max(chartH, table ? (chartData.length + 1) * rowH : 0) + innerPadding;
            } else {
                contentW = innerPadding + Math.max(chartW, table ? tableW : 0) + innerPadding;
                contentH = innerPadding + chartH + (table ? innerPadding + (chartData.length + 1) * rowH : 0) + innerPadding;
            }

            const canvasW = padding * 2 + border * 2 + contentW;
            const canvasH = padding * 2 + border * 2 + contentH + captionGap + captionBlockH;

            const canvas = document.createElement("canvas");
            canvas.width = canvasW;
            canvas.height = canvasH;
            const ctx = canvas.getContext("2d");

            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, canvasW, canvasH);

            const boxX = padding;
            const boxY = padding;
            const boxW = border * 2 + contentW;
            const boxH = border * 2 + contentH;

            ctx.strokeStyle = "#333";
            ctx.lineWidth = border;
            ctx.strokeRect(boxX, boxY, boxW, boxH);

            const startX = boxX + border + innerPadding;
            const startY = boxY + border + innerPadding;

            ctx.font = legendFont;
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.strokeStyle = tableBorderColor;

            if (chartType === "pie") {
                ctx.drawImage(chartCanvas, startX, startY);
                if (table) {
                    const tableX = startX + chartW + innerPadding;
                    const tableY = startY;
                    drawTable(ctx, tableX, tableY, rowH, chartData, columns, colWidths, legendColW, isMulti, totalVal);
                }
            } else {
                ctx.drawImage(chartCanvas, startX, startY);
                if (table) {
                    const tableX = startX;
                    const tableY = startY + chartH + innerPadding;
                    drawTable(ctx, tableX, tableY, rowH, chartData, columns, colWidths, legendColW, isMulti, totalVal);
                }
            }

            ctx.textAlign = "center";
            const captionY = boxY + boxH + captionGap;

            if (imageTitle) {
                ctx.font = titleFont;
                ctx.fillStyle = captionColor;
                ctx.fillText(imageTitle, canvasW / 2, captionY);
            }
            if (imageSource) {
                ctx.font = sourceFont;
                ctx.fillText(imageSource, canvasW / 2, captionY + titleH + betweenCaptionGap);
            }

            canvas.toBlob((b) => saveAs(b, `${fileNamePrefix}_${Date.now()}.png`));
        });
    };

    const computeTableWidth = (ctx, chartData, isMulti) => {
        ctx.font = tableFont;

        let columns = [];
        if (isMulti) {
            const columnSet = new Set();
            chartData.forEach(d => Object.keys(d.values || {}).forEach(k => columnSet.add(k)));
            columns = Array.from(columnSet);
        } else {
            columns = ["Value (%)"];
        }

        let maxLegendW = ctx.measureText(LegendFirstColumn).width;
        chartData.forEach(d => {
            const labelW = ctx.measureText(d.name || d.category || d.year).width + (showColorBlock ? 30 : 0) + 20;
            maxLegendW = Math.max(maxLegendW, labelW);
        });
        maxLegendW += cellPadding * 2;

        const colWidths = columns.map(col => {
            let maxW = ctx.measureText(col).width;
            if (isMulti) {
                chartData.forEach(d => {
                    const val = d.values[col] !== undefined ? `${d.values[col].toFixed(1)}%` : "0.0%";
                    maxW = Math.max(maxW, ctx.measureText(val).width);
                });
            } else {
                chartData.forEach(d => {
                    const pct = ((d.value / chartData.reduce((s, d) => s + (d.value || 0), 0)) * 100).toFixed(1) + "%";
                    maxW = Math.max(maxW, ctx.measureText(pct).width);
                });
            }
            return maxW + cellPadding * 2;
        });

        const tableW = maxLegendW + colWidths.reduce((sum, w) => sum + w, 0);
        return { tableW, columns, colWidths, legendColW: maxLegendW };
    };

    const drawTable = (ctx, tableX, tableY, rowH, chartData, columns, colWidths, legendColW, isMulti, totalVal) => {
        ctx.font = tableFont;

        const totalTableW = legendColW + colWidths.reduce((sum, w) => sum + w, 0);

        ctx.fillStyle = tableHeaderColor;
        ctx.fillRect(tableX, tableY, totalTableW, rowH);
        ctx.strokeStyle = tableBorderColor;
        ctx.strokeRect(tableX, tableY, totalTableW, rowH);

        ctx.fillStyle = captionColor;
        ctx.fillText(LegendFirstColumn, tableX + cellPadding, tableY + rowH / 2);

        let colStartX = tableX + legendColW;
        columns.forEach((col, i) => {
            ctx.fillText(col, colStartX + cellPadding, tableY + rowH / 2);
            colStartX += colWidths[i];
        });

        chartData.forEach((d, rowIndex) => {
            const y = tableY + rowH * (rowIndex + 1);
            ctx.strokeRect(tableX, y, totalTableW, rowH);

            const label = d.name || d.category || d.year;
            const blockColor = getMappedColor(label, d.color || "#000");

            if (showColorBlock) {
                ctx.fillStyle = blockColor;
                ctx.fillRect(tableX + 6, y + 10, 18, 18);
            }

            ctx.fillStyle = captionColor;
            ctx.fillText(label, tableX + (showColorBlock ? 30 : 6), y + rowH / 2);

            let colX = tableX + legendColW;
            columns.forEach((col, i) => {
                let valText = "0.0%";
                if (isMulti) {
                    valText = d.values[col] !== undefined ? `${d.values[col].toFixed(1)}%` : "0.0%";
                } else {
                    valText = ((d.value / totalVal) * 100).toFixed(1) + "%";
                }
                ctx.fillText(valText, colX + cellPadding, y + rowH / 2);
                colX += colWidths[i];
            });
        });
    };

    return null;
});

export default ChartExportAsPNG;
